import React, { useEffect, useState } from 'react';
import Box from 'Components/Atoms/Box';
import './styles.scss';
import Card from 'Components/Molecules/Card';
import Typography from 'Components/Atoms/Typography';
import IconButton from 'Components/Atoms/IconButton';
import { Close } from 'Components/Atoms/Icons';
import { Reservation, Table } from 'types/reservations';
import { classHelper, noop } from 'utils/helper';
import TableButtons from './Components/TableButtons';
import TableSchedule from './Components/TableSchedule';
import { ScheduleBand } from 'utils/tableBlocks';
import TableBlockModal, {
  TableBlockModalProps,
} from 'App/ReservationBook/Components/TableBlockModal';
import { ReservationActionTypes } from 'Contexts/ReservationContext';
import { useTableInfo } from 'CustomHooks/useTableInfo';

export type TableSideBarProps = {
  start: number;
  close: number;
  shiftWindow?: [number, number] | null;
  blocked: boolean;
  scheduleBands: ScheduleBand[];
  onRemoveBlock?: (blockId: string) => void;
  blockModal: TableBlockModalProps;
  reservations: Reservation[];
  table: Table | null;
  name: string;
  onWalkin: (tables: string[], space?: string) => void;
  onToggleBlock: (id: string, name: string) => void;
  perShiftBlocking: boolean;
  onOpenBlockModal: () => void;
  onCloseSideBar: () => void;
  setcurrentReservation: (id: string | null) => void;
  onReservationAction: (
    id: string,
    type: ReservationActionTypes,
    payload?: any
  ) => void;
  id: string;
  currentTime: string | null;
  sidebarFontSize?: string;
};

const TableSideBar: React.FC<TableSideBarProps> = ({
  start,
  close,
  shiftWindow = null,
  blocked = false,
  scheduleBands,
  onRemoveBlock,
  blockModal,
  reservations,
  name,
  onWalkin = noop,
  onToggleBlock = noop,
  perShiftBlocking = false,
  onOpenBlockModal = noop,
  id,
  currentTime = null,
  onCloseSideBar = noop,
  table,
  onReservationAction = noop,
  setcurrentReservation,
  sidebarFontSize,
}) => {
  const [hasOverflow, setHasOverflow] = useState<{
    bottom: boolean;
    top: boolean;
  }>({ top: false, bottom: false });

  const handleScrolling = (
    event: React.UIEvent<HTMLDivElement | HTMLLabelElement | HTMLFormElement>
  ) => {
    if (
      event.currentTarget.scrollHeight - event.currentTarget.scrollTop ===
      event.currentTarget.clientHeight
    ) {
      setHasOverflow({ top: true, bottom: false });
    } else if (event.currentTarget.scrollTop === 0) {
      setHasOverflow({ top: false, bottom: true });
    } else if (
      event.currentTarget.scrollHeight - event.currentTarget.scrollTop >
        event.currentTarget.clientHeight &&
      event.currentTarget.scrollTop > 0
    ) {
      setHasOverflow({ top: true, bottom: true });
    }
  };

  const tableInfo = useTableInfo(name);

  const filteredReservations = reservations.filter((r) => !r.done);

  useEffect(() => {
    if (filteredReservations.length === 1 && filteredReservations[0].started) {
      setcurrentReservation(filteredReservations[0].id);
    }
  }, [tableInfo]);

  return (
    <Card
      style={{
        borderRadius: 0,
        height: '100%',
        fontSize: sidebarFontSize ? `${sidebarFontSize}px` : undefined,
      }}
      hideHeader
      className={classHelper(['sidebar', sidebarFontSize && 'customSidebar'])}
      subHeader={
        <Box padding="md" className="space-between">
          <Box flex>
            <Typography
              variant="h5"
              weight="bold"
              style={{ marginRight: 8 }}
              translation="reservations"
            >
              Table No.
            </Typography>
            <Typography variant="h5" weight="bold" color="primary">
              {name}
            </Typography>
          </Box>
          <IconButton
            color="subdued"
            size="small"
            onClick={() => onCloseSideBar()}
          >
            <Close />
          </IconButton>
        </Box>
      }
      bodyProps={{
        style: {
          position: 'relative',
          height: 'calc(100% - 60px)',
        },
        className: 'space-between flex-column',
      }}
    >
      {hasOverflow.top && <div className="topOverflowGradient"></div>}

      {
        <TableSchedule
          {...{
            start,
            close,
            shiftWindow,
            reservations: filteredReservations,
            currentTime,
            setHasOverflow,
            handleScrolling,
            setcurrentReservation,
            onReservationAction,
            tableInfo,
            shared: !!table?.shared,
            tableMax: table?.max,
            tableName: table?.name,
            bands: scheduleBands,
            onRemoveBlock,
          }}
        />
      }
      {hasOverflow.bottom && <div className="bottomOverflowGradient"></div>}
      <TableButtons
        {...{
          onWalkin,
          onToggleBlock,
          blocked,
          id,
          name,
          space: table?.space,
          perShiftBlocking,
          onOpenBlockModal,
        }}
      />
      <TableBlockModal {...blockModal} />
    </Card>
  );
};

export default TableSideBar;
